import { Box, Button, MenuItem, Typography } from '@mui/material';
import { Plus, Trash } from '@phosphor-icons/react';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { IFlowModel } from '../../../data/models/flow';
import { MuiInputControlled } from '../../Form/Input';
import { MuiSelectControlled } from '../../Form/Select';

interface FormDropdownComponentProps {
  name: string;
  index: number;
  flow: IFlowModel;
}

export function FormDropdownComponent({
  name,
  index,
  flow,
}: Readonly<FormDropdownComponentProps>) {
  const { control, setValue, getValues } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: `${name}[${index}].options`,
  });

  const dataInputNodes = useMemo(() => {
    const dataInputNodes =
      flow.nodes?.filter(node => node.type === 'varInputNode') ?? [];

    return dataInputNodes;
  }, [flow]);

  function handleAddAction() {
    append({
      value: '',
      label: '',
    });
  }

  function handleRemoveAction(index: number) {
    remove(index);
  }

  const handleDropdownName = (index: number) => {
    switch (index) {
      case 0:
        return 'Dropdown Left';
      case 1:
        return 'Dropdown Right';
      case 2:
        return 'Dropdown Center';
      default:
        break;
    }
  };

  useEffect(() => {
    return () => {
      const oldValue = getValues(name);

      setValue(name, oldValue.slice(0, -1));
    };
  }, [name, getValues, setValue]);

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      alignItems="start"
      gap="8px"
      mt="8px"
      mb="8px"
    >
      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: '500',
          color: '#101828',
        }}
      >
        {handleDropdownName(index)}
      </Typography>

      <Box display="flex" alignItems="baseline" gap="8px">
        <MuiInputControlled
          styles={{ minWidth: '200px' }}
          inputProps={{
            name: `${name}[${index}].name`,
            label: 'Dropdown name',
            placeholder: 'Type your dropdown name here',
          }}
        />

        <MuiSelectControlled
          styles={{ minWidth: '200px' }}
          selectProps={{
            name: `${name}[${index}].varInputNodeId`,
            label: 'Data input',
            placeholder: 'Select a data input node',
          }}
        >
          {dataInputNodes.length === 0 && (
            <MenuItem value="" disabled>
              No data input nodes found
            </MenuItem>
          )}

          {dataInputNodes.map(node => (
            <MenuItem key={node.id} value={node.id}>
              {node.data.label}
            </MenuItem>
          ))}
        </MuiSelectControlled>
      </Box>

      {fields.map((field, fieldIndex) => (
        <Box
          width="100%"
          display="flex"
          alignItems="baseline"
          gap="8px"
          key={field.id}
        >
          <MuiInputControlled
            styles={{ minWidth: '200px' }}
            inputProps={{
              name: `${name}[${index}].options[${fieldIndex}].label`,
              label: `Option ${fieldIndex + 1} - Label`,
              placeholder: 'Type your option label here',
            }}
          />

          <MuiInputControlled
            styles={{ width: '100%' }}
            inputProps={{
              name: `${name}[${index}].options[${fieldIndex}].value`,
              label: `Option ${fieldIndex + 1} - Value`,
              placeholder: 'Type your option value here',
            }}
          />

          <Button onClick={() => handleRemoveAction(index)} variant="text">
            <Trash size={20} color="red" />
          </Button>
        </Box>
      ))}

      <Box display="flex" justifyContent="end" width="100%">
        <Button
          variant="outlined"
          sx={{
            width: '160px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
          onClick={handleAddAction}
          disabled={fields.length >= 10}
        >
          Add new <Plus size={16} />
        </Button>
      </Box>
    </Box>
  );
}
